import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  deletePfc,
  fetchPfcCurves,
  fetchPfcPoints,
  fetchPfcVersions,
  fetchPfcWhoami,
  uploadPfc,
  type PfcCurveSummary,
  type PfcPointsResponse,
  type PfcVersion,
  type PfcWhoami,
} from "../lib/api";
import { ChartCard } from "./ChartCard";
import { LineChart } from "./LineChart";
import type { Series } from "../lib/chart";

const PRICE_FMT = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const DATE_FMT = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  timeZone: "Europe/Berlin",
});
const DATETIME_FMT = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "Europe/Berlin",
});

/**
 * Human-readable label for the automatic file-driven curve key.
 * Strips the mandatory `PRICEIT_` prefix and drops the last few
 * segments (SOURCE/MODIFIER/etc.) into a compact secondary line so
 * a wide filename like `PRICEIT_POWER_QPFC_EEX_DE_XC_GREEN_SETTLEMENT_DYN`
 * doesn't blow up the sidebar layout.
 */
function splitCurveKey(k: string): { primary: string; secondary: string } {
  const noPrefix = k.startsWith("PRICEIT_") ? k.slice(8) : k;
  const parts = noPrefix.split("_");
  if (parts.length <= 2) return { primary: noPrefix, secondary: "" };
  return { primary: parts.slice(0, 2).join(" · "), secondary: parts.slice(2).join(" · ") };
}

const GRAN_LABEL: Record<string, string> = {
  quarter_hour: "15 min",
  hourly: "stündlich",
  daily: "täglich",
};

export function UploadedPfcTab() {
  const queryClient = useQueryClient();
  const whoami = useQuery<PfcWhoami, Error>({
    queryKey: ["pfc-whoami"],
    queryFn: fetchPfcWhoami,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  const curves = useQuery<PfcCurveSummary[], Error>({
    queryKey: ["pfc-curves"],
    queryFn: fetchPfcCurves,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const [selectedCurve, setSelectedCurve] = useState<string | null>(null);
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);

  // Auto-select the newest curve + its latest version when data arrives.
  const activeCurveKey = useMemo(() => {
    if (selectedCurve) return selectedCurve;
    return curves.data?.[0]?.curveKey ?? null;
  }, [selectedCurve, curves.data]);

  const versions = useQuery<PfcVersion[], Error>({
    queryKey: ["pfc-versions", activeCurveKey],
    queryFn: () => fetchPfcVersions(activeCurveKey!),
    enabled: !!activeCurveKey,
    staleTime: 30 * 1000,
  });

  const activeVersionId = useMemo(() => {
    if (selectedVersionId) return selectedVersionId;
    return versions.data?.[0]?.id ?? null;
  }, [selectedVersionId, versions.data]);

  const points = useQuery<PfcPointsResponse, Error>({
    queryKey: ["pfc-points", activeVersionId],
    // Cap the initial fetch at 2000 points — enough to render a full
    // daily DPFC (~2500 pts) in one round-trip and enough to sample a
    // 10-year 15-min QPFC (373k pts) into a legible overview. We can
    // add a "load full detail" affordance later if a user needs it.
    queryFn: () => fetchPfcPoints(activeVersionId!, { limit: 2000 }),
    enabled: !!activeVersionId,
    staleTime: 5 * 60 * 1000,
  });

  const activeVersion = versions.data?.find((v) => v.id === activeVersionId) ?? null;
  const chartSeries = useMemo<Series[]>(() => {
    if (!points.data || !activeVersion) return [];
    return [
      {
        id: `pfc-${activeVersion.id}`,
        label: activeVersion.priceDate,
        color: "#0284c7",
        step: activeVersion.granularity === "daily",
        points: points.data.points.map((p) => ({
          x: new Date(p.ts).toISOString(),
          y: p.value,
        })),
      },
    ];
  }, [points.data, activeVersion]);

  const upload = useMutation<PfcVersion, Error, { file: File; sourceNote: string }>({
    mutationFn: ({ file, sourceNote }) => uploadPfc(file, sourceNote),
    onSuccess: (uploaded) => {
      // Invalidate both the curves list (a brand-new curve_key may appear)
      // and the versions list for the affected curve. React-Query re-fetches
      // and the UI re-renders to include the fresh row.
      queryClient.invalidateQueries({ queryKey: ["pfc-curves"] });
      queryClient.invalidateQueries({ queryKey: ["pfc-versions", uploaded.curveKey] });
      setSelectedCurve(uploaded.curveKey);
      setSelectedVersionId(uploaded.id);
    },
  });

  const del = useMutation<PfcVersion, Error, number>({
    mutationFn: (id) => deletePfc(id),
    onSuccess: (deleted) => {
      // The versions list for this curve loses one row, and the curves
      // sidebar loses its version-count / possibly the whole curve if
      // that was the last version. Invalidate both. If the deleted
      // version was the one currently rendered in the chart, drop the
      // selection so we don't render stale points.
      queryClient.invalidateQueries({ queryKey: ["pfc-curves"] });
      queryClient.invalidateQueries({ queryKey: ["pfc-versions", deleted.curveKey] });
      queryClient.removeQueries({ queryKey: ["pfc-points", deleted.id] });
      if (selectedVersionId === deleted.id) setSelectedVersionId(null);
    },
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
      <aside className="space-y-3">
        {whoami.data?.isSuperadmin && <UploadPanel mutation={upload} />}
        <CurveList
          curves={curves.data ?? []}
          isLoading={curves.isLoading}
          isError={curves.isError}
          errorMessage={curves.error?.message}
          selected={activeCurveKey}
          onSelect={(k) => {
            setSelectedCurve(k);
            setSelectedVersionId(null);
          }}
          onRetry={() => curves.refetch()}
        />
      </aside>

      <div className="space-y-4">
        {activeCurveKey ? (
          <>
            <VersionList
              versions={versions.data ?? []}
              isLoading={versions.isLoading}
              isError={versions.isError}
              errorMessage={versions.error?.message}
              selected={activeVersionId}
              onSelect={setSelectedVersionId}
              canDelete={!!whoami.data?.isSuperadmin}
              onDelete={(v) => {
                if (
                  !window.confirm(
                    `Version vom ${new Date(v.priceDate).toLocaleDateString("de-DE")} (${v.pointCount.toLocaleString("de-DE")} Punkte) wirklich löschen?`,
                  )
                ) return;
                del.mutate(v.id);
              }}
              deletingId={del.isPending ? del.variables ?? null : null}
              deleteError={del.error?.message ?? null}
            />
            {activeVersion && (
              <ChartCard
                title={splitCurveKey(activeVersion.curveKey).primary}
                unit="€/MWh"
                accent="sky"
                updatedAt={activeVersion.uploadedAt}
                controls={
                  <span className="text-[11px] text-slate-500 dark:text-slate-400 whitespace-nowrap">
                    Preisstand {DATE_FMT.format(new Date(activeVersion.priceDate))} · {GRAN_LABEL[activeVersion.granularity] ?? activeVersion.granularity} · {activeVersion.pointCount.toLocaleString("de-DE")} Punkte
                  </span>
                }
                footer={
                  points.data?.hasMore ? (
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-2">
                      Zeigt die ersten 2000 Punkte. Für vollständige Analyse den MCP-Endpunkt <code>montel_pfc_get_points</code> nutzen.
                    </p>
                  ) : null
                }
              >
                {points.isLoading ? (
                  <div className="h-64 flex items-center justify-center text-sm text-slate-500 dark:text-slate-400">
                    Lade Datenpunkte…
                  </div>
                ) : chartSeries.length ? (
                  <LineChart series={chartSeries} unit="€/MWh" height={340} />
                ) : (
                  <div className="h-64 flex items-center justify-center text-sm text-slate-500 dark:text-slate-400">
                    Keine Datenpunkte in dieser Version.
                  </div>
                )}
              </ChartCard>
            )}
          </>
        ) : curves.isLoading ? (
          <EmptyState title="Lade Kurven…" />
        ) : (
          <EmptyState
            title="Noch keine PFCs hochgeladen"
            description={
              whoami.data?.isSuperadmin
                ? "Nutze das Upload-Panel oben, um die erste Kurve hinzuzufügen."
                : "Frag einen Administrator, PRICEIT-Kurven hochzuladen. Sie erscheinen dann automatisch hier und stehen den AI-Agents via MCP zur Verfügung."
            }
          />
        )}
      </div>
    </div>
  );
}

function CurveList({
  curves,
  isLoading,
  isError,
  errorMessage,
  selected,
  onSelect,
  onRetry,
}: {
  curves: PfcCurveSummary[];
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  selected: string | null;
  onSelect: (curveKey: string) => void;
  onRetry: () => void;
}) {
  return (
    <section className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
      <header className="px-3 py-2 border-b border-slate-200 dark:border-slate-800 flex items-baseline justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
          Kurven
        </h3>
        <span className="text-[10px] text-slate-500 dark:text-slate-400">
          {curves.length ? `${curves.length} verfügbar` : ""}
        </span>
      </header>
      {isLoading && <p className="p-3 text-xs text-slate-500 dark:text-slate-400">Lade…</p>}
      {isError && (
        <div className="p-3 text-xs">
          <p className="text-rose-700 dark:text-rose-300 mb-1">Fehler beim Laden: {errorMessage}</p>
          <button
            className="text-sky-700 dark:text-sky-300 underline underline-offset-2"
            onClick={onRetry}
            type="button"
          >
            Erneut versuchen
          </button>
        </div>
      )}
      {!isLoading && !isError && curves.length === 0 && (
        <p className="p-3 text-xs text-slate-500 dark:text-slate-400">Noch keine Uploads.</p>
      )}
      <ul>
        {curves.map((c) => {
          const label = splitCurveKey(c.curveKey);
          const isActive = c.curveKey === selected;
          return (
            <li key={c.curveKey}>
              <button
                type="button"
                onClick={() => onSelect(c.curveKey)}
                className={`w-full text-left px-3 py-2 border-t border-slate-100 dark:border-slate-800/60 first:border-t-0 text-xs hover:bg-slate-50 dark:hover:bg-slate-800/40 ${
                  isActive ? "bg-sky-50/60 dark:bg-sky-500/[0.07]" : ""
                }`}
              >
                <div className="font-medium truncate">{label.primary}</div>
                {label.secondary && (
                  <div className="text-[10px] text-slate-500 dark:text-slate-400 truncate">
                    {label.secondary}
                  </div>
                )}
                <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                  {c.versionCount}× · {GRAN_LABEL[c.granularity] ?? c.granularity} · aktuell {DATE_FMT.format(new Date(c.latestPriceDate))}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function VersionList({
  versions,
  isLoading,
  isError,
  errorMessage,
  selected,
  onSelect,
  canDelete,
  onDelete,
  deletingId,
  deleteError,
}: {
  versions: PfcVersion[];
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  selected: number | null;
  onSelect: (id: number) => void;
  canDelete: boolean;
  onDelete: (v: PfcVersion) => void;
  deletingId: number | null;
  deleteError: string | null;
}) {
  if (isLoading) {
    return (
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-3 text-xs text-slate-500 dark:text-slate-400">
        Lade Versionen…
      </div>
    );
  }
  if (isError) {
    return (
      <div className="rounded-xl border border-rose-200 dark:border-rose-900 bg-rose-50 dark:bg-rose-950/40 p-3 text-xs text-rose-800 dark:text-rose-200">
        Fehler: {errorMessage}
      </div>
    );
  }
  if (versions.length === 0) return null;

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm overflow-x-auto">
      {deleteError && (
        <p className="px-3 py-1.5 text-[11px] text-rose-700 dark:text-rose-300 border-b border-rose-200 dark:border-rose-900 bg-rose-50 dark:bg-rose-950/40">
          Löschen fehlgeschlagen: {deleteError}
        </p>
      )}
      <table className="w-full text-[11px] sm:text-xs">
        <thead>
          <tr className="text-left text-[9px] sm:text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-800">
            <th className="px-3 py-2 font-medium">Preisstand</th>
            <th className="px-3 py-2 font-medium">Hochgeladen</th>
            <th className="px-3 py-2 font-medium">Von</th>
            <th className="px-3 py-2 font-medium text-right">Punkte</th>
            <th className="px-3 py-2 font-medium">Zeitraum</th>
            <th className="px-3 py-2 font-medium">Notiz</th>
            {canDelete && <th className="px-3 py-2 font-medium text-right">Aktion</th>}
          </tr>
        </thead>
        <tbody>
          {versions.map((v) => {
            const isActive = v.id === selected;
            const isDeleting = deletingId === v.id;
            return (
              <tr
                key={v.id}
                onClick={() => onSelect(v.id)}
                className={`cursor-pointer border-b border-slate-100 dark:border-slate-800/60 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-800/40 ${
                  isActive ? "bg-sky-50/60 dark:bg-sky-500/[0.07]" : ""
                } ${isDeleting ? "opacity-50" : ""}`}
              >
                <td className="px-3 py-1.5 font-medium tabular-nums whitespace-nowrap">
                  {DATE_FMT.format(new Date(v.priceDate))}
                </td>
                <td className="px-3 py-1.5 whitespace-nowrap text-slate-600 dark:text-slate-400">
                  {DATETIME_FMT.format(new Date(v.uploadedAt))}
                </td>
                <td className="px-3 py-1.5 truncate max-w-[18ch] text-slate-600 dark:text-slate-400" title={v.uploadedBy}>
                  {v.uploadedBy}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums">{v.pointCount.toLocaleString("de-DE")}</td>
                <td className="px-3 py-1.5 whitespace-nowrap text-slate-600 dark:text-slate-400">
                  {DATE_FMT.format(new Date(v.startTs))} – {DATE_FMT.format(new Date(v.endTs))}
                </td>
                <td className="px-3 py-1.5 truncate max-w-[24ch] text-slate-600 dark:text-slate-400" title={v.sourceNote ?? undefined}>
                  {v.sourceNote ?? "—"}
                </td>
                {canDelete && (
                  <td className="px-3 py-1.5 text-right whitespace-nowrap">
                    <button
                      type="button"
                      disabled={isDeleting}
                      onClick={(e) => {
                        // Row click handler otherwise re-selects the version we
                        // are about to delete, which flashes stale points in
                        // the chart until the invalidate lands.
                        e.stopPropagation();
                        onDelete(v);
                      }}
                      className="rounded-md border border-rose-200 dark:border-rose-900 text-rose-700 dark:text-rose-300 bg-white dark:bg-slate-900 hover:bg-rose-50 dark:hover:bg-rose-950/40 disabled:opacity-50 disabled:cursor-not-allowed px-2 py-0.5 text-[11px]"
                      aria-label={`Version vom ${v.priceDate} löschen`}
                    >
                      {isDeleting ? "…" : "Löschen"}
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function UploadPanel({
  mutation,
}: {
  mutation: ReturnType<typeof useMutation<PfcVersion, Error, { file: File; sourceNote: string }>>;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [sourceNote, setSourceNote] = useState("");
  const [dragging, setDragging] = useState(false);

  return (
    <section className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
      <header className="px-3 py-2 border-b border-slate-200 dark:border-slate-800">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
          PFC hochladen
        </h3>
      </header>
      <div className="p-3 space-y-2">
        <label
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            const f = e.dataTransfer.files[0];
            if (f) setFile(f);
          }}
          className={`block cursor-pointer rounded-lg border-2 border-dashed px-3 py-4 text-center text-xs transition-colors ${
            dragging
              ? "border-sky-400 bg-sky-50/60 dark:bg-sky-500/10"
              : "border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/40"
          }`}
        >
          <input
            type="file"
            accept=".csv,text/csv"
            className="sr-only"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          {file ? (
            <div className="space-y-1">
              <div className="font-medium truncate">{file.name}</div>
              <div className="text-slate-500 dark:text-slate-400">
                {(file.size / 1024).toFixed(0)} KiB
              </div>
            </div>
          ) : (
            <div className="text-slate-500 dark:text-slate-400">
              CSV per Drop oder Klick auswählen
              <div className="text-[10px] mt-1">
                <code>PRICEIT_..._YYYYMMDD.csv</code>
              </div>
            </div>
          )}
        </label>
        <input
          type="text"
          placeholder="Notiz (optional)"
          value={sourceNote}
          onChange={(e) => setSourceNote(e.target.value)}
          className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-2.5 py-1.5 text-xs"
        />
        <button
          type="button"
          disabled={!file || mutation.isPending}
          onClick={() => {
            if (file) mutation.mutate({ file, sourceNote });
          }}
          className="w-full rounded-md bg-sky-600 disabled:bg-slate-300 dark:disabled:bg-slate-700 text-white text-xs font-medium px-3 py-1.5 hover:bg-sky-700 disabled:cursor-not-allowed"
        >
          {mutation.isPending ? "Lade hoch…" : "Hochladen"}
        </button>
        {mutation.isError && (
          <p className="text-[11px] text-rose-700 dark:text-rose-300">{mutation.error.message}</p>
        )}
        {mutation.isSuccess && (
          <p className="text-[11px] text-emerald-700 dark:text-emerald-300">
            {PRICE_FMT.format(mutation.data.pointCount)} Punkte gespeichert (ID {mutation.data.id}).
          </p>
        )}
      </div>
    </section>
  );
}

function EmptyState({ title, description }: { title: string; description?: string }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-6 text-center">
      <p className="text-sm font-medium">{title}</p>
      {description && (
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{description}</p>
      )}
    </div>
  );
}
